import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import SectionContact from '../components/SectionContact';
import Bande from '../components/Bande';
import Reveal from '../components/Reveal';
import TitreSection from '../components/TitreSection';
import AppelAction from '../components/AppelAction';
import Faq from '../components/Faq';
import Avis from '../components/Avis';
import { ENTREPRISE, FAQ } from '../data/site';

const jsonLdFaq = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.filter((f) => f.cat === 'Conduite et sécurité').map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.r },
  })),
};

export default function SansChauffeur() {
  return (
    <>
      <Seo
        titre="Location d'engins avec ou sans chauffeur | CACES, assurance, caution"
        description="Louer un engin de chantier sans chauffeur : CACES R482, autorisation de conduite, assurance, caution, livraison. Ce qu'un professionnel doit savoir avant de réserver en Île-de-France."
        jsonLd={jsonLdFaq}
      />

      <section className="bg-nuit text-white relative overflow-hidden">
        <div className="absolute inset-0 trame-diagonale" aria-hidden="true" />
        <div className="conteneur relative py-14 sm:py-16">
          <h1 className="font-display uppercase text-[36px] sm:text-[48px] leading-[1] max-w-3xl">
            Vous avez les équipes.
            <br />
            Nous avons les <span className="mot-degrade">machines</span>.
          </h1>
          <p className="mt-5 text-white/75 max-w-2xl text-[18px]">
            La location sans chauffeur, c'est notre métier de base. Voici précisément
            ce que ça implique pour vous, sans zone d'ombre et sans surprise au retour.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Link to="/contact" className="btn-jaune">Demander mon devis</Link>
            <a href={ENTREPRISE.telephoneLien} className="btn-contour">{ENTREPRISE.telephone}</a>
          </div>
        </div>
        <Bande hauteur={6} />
      </section>

      <section className="bg-white py-14 sm:py-16">
        <div className="conteneur">
          <TitreSection avant="Sans chauffeur, ou " mot="avec" />
          <div className="mt-9 grid gap-6 md:grid-cols-2">
            <Reveal>
              <div className="carte p-7 h-full border-t-4 border-t-jaune">
                <h3 className="font-display uppercase text-[24px] text-nuit">Sans chauffeur</h3>
                <p className="mt-3 text-nuit/75">
                  On vous livre la machine, vos équipes la conduisent. La formule la plus économique
                  quand vous avez du personnel formé.
                </p>
                <ul className="mt-5 space-y-2.5 text-[16px]">
                  {[
                    'Conducteur titulaire du CACES adapté',
                    "Autorisation de conduite délivrée par son employeur",
                    'Aptitude médicale à jour',
                    'La garde du matériel vous revient pendant la location',
                  ].map((x) => (
                    <li key={x} className="flex gap-3">
                      <span className="mt-2 shrink-0 w-2 h-2 bg-bleu" aria-hidden="true" />
                      <span className="text-nuit/85">{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delai={0.08}>
              <div className="carte p-7 h-full border-t-4 border-t-bleu">
                <h3 className="font-display uppercase text-[24px] text-nuit">Avec chauffeur</h3>
                <p className="mt-3 text-nuit/75">
                  La machine arrive avec un conducteur qui la connaît. Pas de question de CACES
                  ni d'autorisation de conduite à gérer de votre côté.
                </p>
                <ul className="mt-5 space-y-2.5 text-[16px]">
                  {[
                    'Idéal quand vos conducteurs sont déjà sur un autre chantier',
                    "Utile pour une intervention courte et technique",
                    'Un seul interlocuteur pour la machine et pour le conducteur',
                    'À préciser au moment du devis',
                  ].map((x) => (
                    <li key={x} className="flex gap-3">
                      <span className="mt-2 shrink-0 w-2 h-2 bg-jaune" aria-hidden="true" />
                      <span className="text-nuit/85">{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <AppelAction titre="Vous ne savez pas quelle formule choisir ?" texte="Dites-nous qui conduit et ce qu'il y a à faire. On vous oriente sans vous vendre l'option la plus chère." />

      <section className="bg-white py-14 sm:py-16">
        <div className="conteneur">
          <TitreSection avant="Le CACES, expliqué " mot="simplement" />
          <div className="mt-8 max-w-3xl space-y-5 text-nuit/85 text-[17px]">
            <p>
              L'obligation légale, ce n'est pas le CACES en lui-même : c'est{' '}
              <strong className="font-semibold">l'autorisation de conduite</strong>, prévue par
              l'article R4323-56 du Code du travail. Elle est délivrée par l'employeur du conducteur,
              jamais par le loueur.
            </p>
            <p>
              Cette autorisation repose sur trois éléments : une aptitude médicale, un contrôle des
              connaissances et du savoir-faire, et la connaissance du site où la machine va travailler.
              Le CACES est le moyen recommandé de valider le deuxième point, mais il ne remplace pas
              l'autorisation.
            </p>
          </div>

          <div className="mt-9 overflow-x-auto">
            <table className="w-full min-w-[600px] text-[16px] border border-nuit/10">
              <thead className="bg-nuit text-white">
                <tr>
                  <th className="text-left font-cond uppercase tracking-[0.06em] text-[15px] px-5 py-3">Machine</th>
                  <th className="text-left font-cond uppercase tracking-[0.06em] text-[15px] px-5 py-3">Catégorie</th>
                  <th className="text-left font-cond uppercase tracking-[0.06em] text-[15px] px-5 py-3">Pourquoi</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Mini-pelles Volvo ECR25D, ECR50D et Sunward SWE25UF', 'R482 / R482A catégorie A', 'Engins compacts de moins de 6 tonnes'],
                  ['Chariot télescopique JCB 525-60', 'R482 / R482A catégorie F', 'Chariot de manutention de chantier, et non chariot industriel'],
                ].map((l) => (
                  <tr key={l[0]} className="border-t border-nuit/10">
                    <td className="px-5 py-3.5 font-semibold text-nuit">{l[0]}</td>
                    <td className="px-5 py-3.5 text-bleu font-semibold">{l[1]}</td>
                    <td className="px-5 py-3.5 text-nuit/75">{l[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 carte p-6 border-l-4 border-l-jaune max-w-3xl">
            <p className="text-nuit/85">
              <strong className="font-semibold">Deux précisions utiles.</strong> Un chariot
              télescopique de chantier relève de la R482 catégorie F, pas de la R489 : c'est une
              confusion fréquente et coûteuse. Et depuis le 1er décembre 2025, la recommandation
              R482 est remplacée par la R482A, sans effet rétroactif : les CACES en cours restent
              valables jusqu'à leur échéance.
            </p>
          </div>
        </div>
      </section>

      <AppelAction variante="sombre" titre="Un doute sur les habilitations de vos équipes ?" texte="Appelez-nous avant de commander. Si ça ne colle pas, on vous propose la machine avec chauffeur." />

      <section className="bg-white py-14 sm:py-16">
        <div className="conteneur">
          <TitreSection avant="Ce qu'on vous remet, ce qui vous " mot="incombe" />
          <div className="mt-9 grid gap-8 md:grid-cols-2">
            <div>
              <h3 className="font-cond uppercase font-bold tracking-[0.05em] text-[19px] text-nuit">
                Ce que nous fournissons
              </h3>
              <ul className="mt-4 space-y-3">
                {[
                  'Une machine révisée et contrôlée avant chaque départ',
                  'Le rapport de vérification générale périodique à jour',
                  "La notice d'utilisation du constructeur",
                  'Les documents de conformité de la machine',
                  "Un état des lieux contradictoire au départ et au retour",
                  "Le conseil sur la machine adaptée à votre chantier",
                ].map((x) => (
                  <li key={x} className="flex gap-3">
                    <span className="mt-2 shrink-0 w-2.5 h-2.5 bg-jaune" aria-hidden="true" />
                    <span className="text-nuit/85">{x}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-cond uppercase font-bold tracking-[0.05em] text-[19px] text-nuit">
                Ce qui reste de votre côté
              </h3>
              <ul className="mt-4 space-y-3">
                {[
                  'Un conducteur formé et titulaire de son autorisation de conduite',
                  'La garde du matériel pendant toute la durée de la location',
                  'Les contrôles journaliers et le respect des consignes du constructeur',
                  'La restitution de la machine en bon état, nettoyée, plein fait',
                  "La conservation du rapport de vérification pendant la location",
                ].map((x) => (
                  <li key={x} className="flex gap-3">
                    <span className="mt-2 shrink-0 w-2.5 h-2.5 bg-bleu" aria-hidden="true" />
                    <span className="text-nuit/85">{x}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-8 max-w-3xl text-nuit/75">
            Le détail de l'assurance, de la franchise et du dépôt de garantie figure sur votre devis
            et dans le contrat de location, avant toute signature. Rien ne se découvre après coup.
          </p>
        </div>
      </section>

      <Avis />

      <Faq questions={FAQ.filter((f) => f.cat === 'Conduite et sécurité')} avant="Le CACES et la conduite, vos " mot="questions" />
      <SectionContact
        avant="Avec ou sans chauffeur, on en "
        mot="parle ?"
        intro="Dites-nous qui conduit et ce qu'il y a à faire. On vous oriente sans vous vendre l'option la plus chère."
      />
    </>
  );
}
